import { apiGet, apiPost } from "./client";

export type ApprovalCategory = "operational" | "destructive";

export type OverrideRequest = {
  id: string;
  permissionKey: string;
  category: ApprovalCategory;
  status: string;
  summary: string | null;
  grantScope: string | null;
  grantExpiresAt: string | null;
  requesterUserId: string;
  approverUserId: string | null;
  docType?: string | null;
  docId?: string | null;
  terminalId?: string | null;
  payload?: Record<string, unknown> | null;
  createdAt: string;
  decidedAt: string | null;
};

export function fetchOverrideRequest(
  companyRef: string,
  requestId: string,
) {
  return apiGet<{ request: OverrideRequest }>(
    `/c/${companyRef}/overrides/requests/${requestId}`,
  );
}

export function listOverrideRequests(
  companyRef: string,
  status = "pending",
) {
  return apiGet<{
    requests: OverrideRequest[];
    counts: {
      pending: number;
      operational: number;
      destructive: number;
    };
  }>(
    `/c/${companyRef}/overrides/requests?status=${encodeURIComponent(status)}`,
  );
}

export function createOverrideRequest(
  companyRef: string,
  body: {
    permissionKey: string;
    summary?: string;
    payload?: Record<string, unknown>;
    terminalId?: string;
    docType?: string;
    docId?: string;
    autoApprove?: boolean;
    grantScope?: "transaction" | "hour" | "today";
  },
) {
  return apiPost<{
    request: {
      id: string;
      status: string;
      permissionKey: string;
      category: ApprovalCategory;
      grantScope: string | null;
    };
  }>(`/c/${companyRef}/overrides/requests`, body);
}

export function approveOverrideRequest(
  companyRef: string,
  requestId: string,
  body?: {
    grantScope?: "transaction" | "hour" | "today";
    decisionNote?: string;
  },
) {
  return apiPost<{
    request: {
      id: string;
      status: string;
      category: ApprovalCategory;
      grantScope: string;
    };
  }>(`/c/${companyRef}/overrides/requests/${requestId}/approve`, body ?? {});
}

/** Manager PIN on the same terminal — does not switch cashier session. */
export function approveOverrideWithPin(
  companyRef: string,
  requestId: string,
  body: {
    userCode: string;
    pin: string;
    grantScope?: "transaction" | "hour" | "today";
    decisionNote?: string;
  },
) {
  return apiPost<{
    request: {
      id: string;
      status: string;
      category: ApprovalCategory;
      grantScope: string;
      grantExpiresAt: string | null;
      approverUserId: string;
    };
  }>(
    `/c/${companyRef}/overrides/requests/${requestId}/approve-pin`,
    body,
  );
}

export function denyOverrideRequest(
  companyRef: string,
  requestId: string,
  body?: { decisionNote?: string },
) {
  return apiPost<{ request: { id: string; status: string } }>(
    `/c/${companyRef}/overrides/requests/${requestId}/deny`,
    body ?? {},
  );
}

/** Manager: mint emergency OTP for a pending override (plan/14). */
export function mintOverrideOtp(
  companyRef: string,
  requestId: string,
  body?: { grantScope?: "transaction" | "hour" | "today" },
) {
  return apiPost<{
    otp: {
      code: string;
      expiresAt: string;
      permissionKey: string;
      grantScope: string;
      requestId: string;
    };
  }>(`/c/${companyRef}/overrides/requests/${requestId}/otp`, body ?? {});
}

export type PregenWalletSlot = {
  id: string;
  permissionKey: string;
  expiresAt: string;
  createdAt: string;
};

/** Unused pregen codes for the current manager (no plaintext). */
export function listOtpWallet(companyRef: string) {
  return apiGet<{ codes: PregenWalletSlot[] }>(
    `/c/${companyRef}/overrides/otp-wallet`,
  );
}

/** Mint a permission-scoped pregen batch — plaintext once. */
export function mintOtpWallet(
  companyRef: string,
  body: { permissionKey: string; count?: number; ttlHours?: number },
) {
  return apiPost<{
    codes: Array<{
      id: string;
      code: string;
      permissionKey: string;
      expiresAt: string;
    }>;
  }>(`/c/${companyRef}/overrides/otp-wallet`, body);
}

/** Cashier: redeem emergency OTP → request approved. */
export function redeemOverrideOtp(
  companyRef: string,
  body: {
    code: string;
    requestId?: string;
    requestKind?: "override" | "change";
  },
) {
  return apiPost<{
    request: {
      id: string;
      status: string;
      category: ApprovalCategory;
      grantScope?: string;
      permissionKey: string;
      requestKind?: "override" | "change";
    };
  }>(`/c/${companyRef}/overrides/redeem-otp`, body);
}
